import React from "react";
import { Box, FormHelperText, Tooltip } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { InfoOutlined } from "@mui/icons-material";
import SecondarySelect from "../../../../components/custom-select/secondary-select";
import MaskedInput from "../../../../components/custom-input/masked-input";
import OutlinedTextInput from "../../../../components/custom-input/outlined-text-input";
import MultiSelectDropdown from "../../../../components/multi-select-dropdown";
import Body2 from "../../../../components/typography/body-02";
import Body3 from "../../../../components/typography/body-03";
import FormSection from "../../../../components/dynamic-form/FormSection";
import FormField from "../../../../components/dynamic-form/FormField";
import RequiredError from "../../../../components/required-error";
import { generateFieldId } from "../../../../utils/formId";
import { PRODUCT_INPUTS_FORM_CONFIG } from "./productInputs.form.config";
import {
	PRODUCT_INPUTS_FORM_NAME,
} from "../../../../utils/aura.constants";

// Renders a single form field using the shared component library.
// Completely independent from the Quick Pricer RenderField component.
const PiField = ({ field, formState, setField, errors, submitted }) => {
	const fieldError = submitted && errors[field.name];

	const clearError = () => {
		// errors are cleared externally via setErrors in the hook
	};

	const isDisabled =
		typeof field.disabled === "function"
			? field.disabled(formState)
			: field.disabled || false;

	const fieldId = generateFieldId(PRODUCT_INPUTS_FORM_NAME, "B", field.name, field.component);

	const isRequired =
		typeof field.required === "function"
			? field.required(formState)
			: !!field.required;

	// Info control beside the label (PathA-3.09 / 3.10).
	const info = typeof field.info === "function" ? field.info(formState) : field.info;
	// Optional note beneath the input (e.g. DTI not calculated yet); a validation error wins.
	const note = typeof field.note === "function" ? field.note(formState) : field.note;

	const label = (
		<>
			{field.label}
			{isRequired && <span style={{ color: "red", marginLeft: 2 }}>*</span>}
			{info && (
				<Tooltip title={info} arrow>
					<InfoOutlined
						fontSize="inherit"
						aria-label={info}
						data-testid={`${field.name}-info`}
						sx={{ marginLeft: "4px", verticalAlign: "middle", cursor: "help" }}
					/>
				</Tooltip>
			)}
			{field.xmlField && (
				<span
					style={{
						marginLeft: 6,
						fontSize: 10,
						fontWeight: 600,
						background: "#e0f2fe",
						color: "#0369a1",
						borderRadius: 4,
						padding: "1px 5px",
						verticalAlign: "middle",
					}}
				>
					XML
				</span>
			)}
			{field.creditReissueField && (
				<span
					style={{
						marginLeft: 6,
						fontSize: 10,
						fontWeight: 600,
						background: "#f0fdf4",
						color: "#15803d",
						borderRadius: 4,
						padding: "1px 5px",
						verticalAlign: "middle",
					}}
				>
					Credit re-issue
				</span>
			)}
			{field.syncedField && (
				<span
					style={{
						marginLeft: 6,
						fontSize: 10,
						fontWeight: 600,
						background: "#f3e8ff",
						color: "#7e22ce",
						borderRadius: 4,
						padding: "1px 5px",
						verticalAlign: "middle",
					}}
				>
					Synced from Dashboard
				</span>
			)}
		</>
	);

	switch (field.component) {
		case "select": {
			const resolvedOptions =
				typeof field.options === "function"
					? field.options(formState)
					: field.options || [];
			return (
				<FormField label={label} htmlFor={fieldId} isFullWidth={field.isFullWidth}>
					<SecondarySelect
						id={fieldId}
						value={formState[field.name] ?? ""}
						options={resolvedOptions}
						handleChange={(e) => {
							setField(field.name)(e.target.value);
						}}
						disabled={isDisabled}
						error={Boolean(fieldError)}
						helperText={fieldError}
					/>
				</FormField>
			);
		}

		case "masked":
			return (
				<FormField label={label} htmlFor={fieldId} isFullWidth={field.isFullWidth}>
					<MaskedInput
						id={fieldId}
						type={field.maskType}
						placeholder={field.placeholder}
						value={formState[field.name] ?? ""}
						// Read-only fields (DTI from Encompass, computed LTVs) are set by code; the
						// mask re-formatting them must not write back and change the value.
						onInputChange={(val) => {
							if (field.disabled === true) return;
							setField(field.name)(val);
						}}
						disabled={isDisabled}
						error={Boolean(fieldError)}
						helperText={fieldError}
					/>
					{!fieldError && note && <FormHelperText>{note}</FormHelperText>}
				</FormField>
			);

		case "numeric":
			return (
				<FormField label={label} htmlFor={fieldId} isFullWidth={field.isFullWidth}>
					<OutlinedTextInput
						id={fieldId}
						type="text"
						placeholder={field.placeholder || ""}
						inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
						value={formState[field.name] ?? ""}
						handleChange={(e) => {
							if (!/^\d*$/.test(e.target.value)) return;
							setField(field.name)(e.target.value);
						}}
						disabled={isDisabled}
						error={Boolean(fieldError)}
						helperText={fieldError}
					/>
				</FormField>
			);

		case "readonly":
			return (
				<FormField label={label} htmlFor={fieldId} isFullWidth={field.isFullWidth}>
					<OutlinedTextInput
						id={fieldId}
						type="text"
						value={formState[field.name] ?? ""}
						disabled
					/>
				</FormField>
			);

		case "autocomplete-multi":
			return (
				<FormField label={label} htmlFor={fieldId} isFullWidth={field.isFullWidth}>
					<MultiSelectDropdown
						options={field.options || []}
						value={formState[field.name] ?? []}
						disabled={isDisabled}
						error={Boolean(fieldError)}
						helperText={fieldError}
						onChange={(val) => setField(field.name)(val)}
						variant="outlined"
						showEndIcon
						endIcon={
							<KeyboardArrowDownIcon
								sx={{ fontSize: 20, color: "action.active", pointerEvents: "none" }}
							/>
						}
					/>
				</FormField>
			);

		default:
			return null;
	}
};

const ProductInputsPanel = ({ formState, setField, errors, submitted }) => (
	<Box>
		{PRODUCT_INPUTS_FORM_CONFIG.sections.map((section, si) => (
			<React.Fragment key={section.name}>
				<FormSection title={section.title}>
					{section.fields
						.filter((field) =>
							field.visibleIf ? field.visibleIf(formState) : true
						)
						.map((field) =>
							field.descriptionSection ? (
								<Box key={field.name} sx={{ width: "100%", mb: 1, mt: 1 }}>
									<Body3 text={field.descriptionSection} />
								</Box>
							) : (
								<PiField
									key={field.name}
									field={field}
									formState={formState}
									setField={setField}
									errors={errors}
									submitted={submitted}
								/>
							)
						)}
				</FormSection>
			</React.Fragment>
		))}
	</Box>
);

export default ProductInputsPanel;
